import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, CheckCircle2, ShieldCheck, FlaskConical, Globe, Building2, ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

const MystiCertified = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const benefits = [
    { icon: ShieldCheck, label: 'Official recognition as a verified Malaysian-developed technology product.' },
    { icon: Building2, label: 'Stronger credibility with government agencies, councils and enterprise buyers.' },
    { icon: FlaskConical, label: 'Acknowledges genuine local research, development and innovation.' },
    { icon: Globe, label: 'Supports Malaysia\u2019s digital economy and home-grown technology ecosystem.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="mysti-page">
      <SEO
        title="Certified by MySTI | StayMii"
        description="StayMii is MySTI Certified \u2014 recognised as a Malaysian Science, Technology and Innovation product (Certification No. STI202602151)."
        canonicalUrl="/certified-by-mysti"
      />
      <LandingNavBar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] pt-32 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-28 h-28 bg-white rounded-3xl mb-6 p-3 shadow-lg">
            <img src="/mysti-certified.png" alt="MySTI Certified" className="w-full h-full object-contain" data-testid="mysti-logo" />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 mb-4">
            <Award className="h-4 w-4 text-emerald-400" />
            <span className="text-emerald-300 text-xs font-semibold tracking-wide">CERTIFIED BY MySTI</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight" style={heading} data-testid="mysti-title">
            Certified by MySTI
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            StayMii is officially recognised as a Malaysian Science, Technology and Innovation product.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-16 md:py-20 space-y-16">

        {/* WHAT IS MySTI */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-[#2563EB]" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading} data-testid="mysti-about-heading">
              What is MySTI Certification?
            </h2>
          </div>
          <div className="space-y-4">
            <p className="text-slate-600 leading-relaxed">
              MySTI (Malaysian Science, Technology and Innovation) certification recognises products and solutions
              that are genuinely developed in Malaysia through local science, technology and innovation. It verifies
              that a product is a credible, home-grown technology offering.
            </p>
            <p className="text-slate-600 leading-relaxed">
              StayMii has been awarded MySTI certification — confirming that our hospitality technology platform,
              including e-Ticketing, the Booking Engine, PMS connectivity and Malaysian e-Invoicing, is a recognised
              Malaysian-developed innovation.
            </p>
          </div>
        </section>

        {/* CERTIFICATION DETAILS */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading}>
              Certification Details
            </h2>
          </div>

          <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden" data-testid="mysti-details-table">
            <div className="divide-y divide-slate-200">
              {[
                ['Certification', 'MySTI \u2014 Malaysian Science, Technology and Innovation'],
                ['Certification No.', 'STI202602151'],
                ['Certified Product', 'StayMii \u2014 Cloud Hospitality Management Platform'],
                ['Category', 'Software / Technology Product'],
                ['Certificate Holder', 'MII Global Partner (202601028203 (1690298-K))'],
                ['Country of Origin', 'Malaysia'],
              ].map(([k, v], i) => (
                <div key={i} className="flex flex-col sm:flex-row">
                  <div className="sm:w-[200px] shrink-0 px-6 py-4 bg-slate-100/80 font-semibold text-slate-700 text-sm">{k}</div>
                  <div className="flex-1 px-6 py-4 text-slate-600 text-sm">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OFFICIAL CERTIFICATE */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Award className="w-5 h-5 text-[#2563EB]" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading} data-testid="mysti-certificate-heading">
              Official Certificate
            </h2>
          </div>
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 sm:p-6">
            <a
              href="/mysti-certificate.png"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mysti-certificate-link"
              className="block group"
            >
              <img
                src="/mysti-certificate.png"
                alt="StayMii MySTI Certification Certificate (No. STI202602151)"
                className="w-full h-auto rounded-xl border border-slate-200 shadow-sm transition-transform group-hover:scale-[1.01]"
                data-testid="mysti-certificate-image"
              />
            </a>
            <p className="text-center text-slate-400 text-xs mt-4">
              Tap the certificate to view the full-size image.
            </p>
          </div>
        </section>

        {/* WHAT IT MEANS */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Award className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={heading}>
              What This Means for You
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed mb-8">
            Choosing a MySTI-certified platform like StayMii means partnering with a recognised Malaysian innovation:
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center border border-slate-200 shrink-0">
                  <item.icon className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-sm font-medium text-slate-700 leading-relaxed">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-8 md:p-10 text-center">
          <Globe className="w-7 h-7 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-xl md:text-2xl font-bold text-white mb-3" style={heading}>
            A proudly Malaysian-built platform
          </h3>
          <p className="text-white/60 text-sm leading-relaxed max-w-lg mx-auto mb-6">
            StayMii is committed to advancing Malaysia's tourism and hospitality industry through locally developed technology.
          </p>
          <Link
            to="/get-started"
            data-testid="mysti-cta-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm transition-colors"
          >
            Get Started with StayMii
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>

      <LandingFooter />
    </div>
  );
};

export default MystiCertified;
